/**
 * MentionPicker — floating dropdown that appears while typing "@..." in a
 * comment/textarea. Searches users and inserts "@Name " into the input on click.
 *
 * Props:
 *   value: current input value
 *   onChange(newValue): called when the user picks a suggestion (replaces the
 *                       partial "@fragment" with the full "@Name ")
 *   inputRef: ref to the underlying <input>/<textarea> so we can position under it
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Returns { start, fragment } if the caret sits inside a partial @token, else null.
function detectMention(value, caret) {
  if (!value || caret == null) return null;
  const before = value.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  // "@ must be at start OR preceded by whitespace"
  if (at > 0 && !/\s/.test(before[at - 1])) return null;
  const fragment = before.slice(at + 1);
  if (fragment.length > 20) return null;
  if (/\s/.test(fragment)) return null;
  return { start: at, fragment };
}

export default function MentionPicker({ value, onChange, inputRef, onPick }) {
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(0);
  const [mention, setMention] = useState(null);
  const abortRef = useRef(null);

  // Watch caret position + value to detect mentions
  useEffect(() => {
    const check = () => {
      const el = inputRef?.current;
      if (!el) { setMention(null); return; }
      const caret = el.selectionStart;
      const m = detectMention(value, caret);
      setMention(m);
    };
    check();
    const el = inputRef?.current;
    if (!el) return;
    el.addEventListener("keyup", check);
    el.addEventListener("click", check);
    return () => {
      el.removeEventListener("keyup", check);
      el.removeEventListener("click", check);
    };
  }, [value, inputRef]);

  // Fetch suggestions when a mention is active
  useEffect(() => {
    if (!mention) { setSuggestions([]); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    axios.get(`${API}/users`, { params: { q: mention.fragment, limit: 6 }, signal: ctrl.signal })
      .then((r) => { setSuggestions(r.data || []); setActive(0); })
      .catch(() => {});
    return () => ctrl.abort();
  }, [mention?.fragment]); // eslint-disable-line

  // Insert selected user into the input
  const pick = (u) => {
    if (!mention) return;
    const caret = inputRef?.current?.selectionStart ?? value.length;
    const before = value.slice(0, mention.start);
    const after = value.slice(caret);
    const inserted = `@${u.name.split(" ")[0]} `;
    const next = `${before}${inserted}${after}`;
    onChange(next);
    onPick?.(u);
    setMention(null);
    setTimeout(() => {
      const el = inputRef?.current;
      if (el) {
        const pos = before.length + inserted.length;
        el.focus();
        try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
      }
    }, 0);
  };

  // Arrow-key navigation
  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    const onKey = (e) => {
      if (!mention || !suggestions.length) return;
      if (e.key === "ArrowDown") { e.preventDefault(); e.stopPropagation(); setActive((a) => (a + 1) % suggestions.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); e.stopPropagation(); setActive((a) => (a - 1 + suggestions.length) % suggestions.length); }
      else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); e.stopPropagation(); pick(suggestions[active]); }
      else if (e.key === "Escape") { e.stopPropagation(); setMention(null); }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [mention, suggestions, active, inputRef]); // eslint-disable-line

  if (!mention || !suggestions.length) return null;
  return (
    <div
      data-testid="mention-picker"
      className="absolute z-50 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-[220px] max-w-[280px]"
      style={{ bottom: "100%", left: 0, marginBottom: 4 }}
    >
      {suggestions.map((u, i) => (
        <button
          key={u.user_id}
          data-testid={`mention-suggestion-${u.user_id}`}
          onMouseDown={(e) => { e.preventDefault(); pick(u); }}
          onMouseEnter={() => setActive(i)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${i === active ? "bg-accent" : ""}`}
        >
          <Avatar className="w-7 h-7"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
          <span className="truncate">{u.name}</span>
        </button>
      ))}
    </div>
  );
}
